import { useState } from 'react';
import { 
  HelpCircle, 
  Search, 
  Book, 
  Package, 
  Truck, 
  Users, 
  MapPin, 
  DollarSign, 
  FileText, 
  Wrench, 
  Shield, 
  BookOpen, 
  BarChart3, 
  Settings,
  ChevronRight,
  Home,
  Play,
  CheckCircle,
  AlertCircle,
  Clock,
  Container,
  Building2,
  Navigation,
  Send,
  Minus,
  Plus,
  Fuel,
  Receipt,
  Activity
} from 'lucide-react';

interface HelpArticle {
  id: string;
  title: string;
  category: string;
  icon: any;
  content: string;
  steps?: string[];
  tips?: string[];
  relatedArticles?: string[];
}

export function HelpCenterView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: Play, color: 'bg-green-100 text-green-600' },
    { id: 'loads', name: 'Loads & Dispatching', icon: Package, color: 'bg-blue-100 text-blue-600' },
    { id: 'safety', name: 'Safety', icon: Shield, color: 'bg-orange-100 text-orange-600' },
    { id: 'accounting', name: 'Accounting', icon: DollarSign, color: 'bg-green-100 text-green-600' },
    { id: 'address-book', name: 'Address Book', icon: BookOpen, color: 'bg-indigo-100 text-indigo-600' },
    { id: 'documents', name: 'Documents', icon: FileText, color: 'bg-slate-100 text-slate-600' },
    { id: 'analytics', name: 'Analytics', icon: BarChart3, color: 'bg-pink-100 text-pink-600' },
    { id: 'settings', name: 'Settings', icon: Settings, color: 'bg-slate-100 text-slate-600' },
  ];

  const articles: HelpArticle[] = [
    // Getting Started
    {
      id: 'dashboard-overview',
      title: 'Dashboard Overview',
      category: 'getting-started',
      icon: Home,
      content: 'The dashboard is your central hub for monitoring all Apollo TMS operations. It displays key metrics, recent activities, and quick access to important features.',
      steps: [
        'View real-time statistics for loads, dispatches, drivers, and trucks',
        'Monitor revenue and expenses',
        'Check active loads and available drivers',
        'Access quick actions for common tasks',
        'Review recent activity and notifications'
      ],
      tips: [
        'Customize your dashboard widgets by clicking the settings icon',
        'Use the search bar at the top to quickly find loads, drivers, or trucks',
        'Click on any metric card to view detailed information'
      ]
    },
    {
      id: 'navigation',
      title: 'Navigating the System',
      category: 'getting-started',
      icon: MapPin,
      content: 'Learn how to navigate through different sections of Apollo TMS efficiently.',
      steps: [
        'Use the left sidebar to access main features',
        'Click on grouped items to expand submenus',
        'Use the search bar for quick access to specific records',
        'Access settings and user profile from the bottom navigation',
        'Use breadcrumbs to track your location in the system'
      ]
    },

    // Loads & Dispatching
    {
      id: 'excel-dispatch-board',
      title: 'Using the Excel Dispatch Board',
      category: 'loads',
      icon: Package,
      content: 'The Excel Dispatch Board provides a spreadsheet-style view of all loads and dispatches for easy management and overview.',
      steps: [
        'Navigate to Loads & Dispatching → Board',
        'View all loads in a spreadsheet format',
        'Filter and sort loads by various criteria',
        'Click on any load to view or edit details',
        'Use the board for quick overview of operations',
        'Export data if needed'
      ],
      tips: [
        'Use filters to focus on specific load statuses',
        'Sort columns to organize data your way',
        'The board updates in real-time',
        'Great for getting a quick overview of all operations'
      ],
      relatedArticles: ['create-load', 'dispatch-board', 'tracking']
    },
    {
      id: 'samsara-location',
      title: 'Real-Time Truck Location with Samsara',
      category: 'loads',
      icon: MapPin,
      content: 'Track your entire fleet in real-time using Samsara GPS integration.',
      steps: [
        'Navigate to Samsara Location from the main menu',
        'View map with all active trucks',
        'Click on any truck marker to see details',
        'View current location, speed, and status',
        'Check driver information and current assignment',
        'Monitor truck movements in real-time',
        'Use filters to show specific trucks or companies'
      ],
      tips: [
        'Samsara integration provides real-time GPS data',
        'Use this to provide accurate ETAs to customers',
        'Monitor for route deviations or delays',
        'Track multiple trucks simultaneously',
        'Verify truck locations before dispatch assignments'
      ],
      relatedArticles: ['tracking', 'dispatches-view', 'eld-integration']
    },
    {
      id: 'create-load',
      title: 'How to Create a Load',
      category: 'loads',
      icon: Package,
      content: 'Creating a load is the first step in managing your freight operations. Follow these steps to create a new load.',
      steps: [
        'Navigate to Loads & Dispatching → Loads',
        'Click the "New Load" button in the top right',
        'Enter load details: Load number, customer, pickup/delivery locations',
        'Add pickup and delivery dates/times',
        'Enter rate, miles, and commodity information',
        'Add any special instructions or requirements',
        'Click "Create Load" to save'
      ],
      tips: [
        'Use the customer search to quickly find existing customers',
        'Save frequently used locations in Address Book for faster entry',
        'Add reference numbers to help track loads with customers',
        'Use the notes field for special handling instructions'
      ],
      relatedArticles: ['create-load', 'create-dispatch', 'load-documents']
    },
    {
      id: 'edit-load',
      title: 'How to Edit a Load',
      category: 'loads',
      icon: Package,
      content: 'Update load information as details change or corrections are needed.',
      steps: [
        'Go to Loads & Dispatching → Loads',
        'Find the load using search or filters',
        'Click on the load to open details',
        'Click the "Edit" button',
        'Update the necessary fields',
        'Click "Save Changes"'
      ],
      tips: [
        'Changes to assigned loads will notify the driver',
        'Update status as the load progresses',
        'Add notes to document any changes made'
      ],
      relatedArticles: ['create-load', 'delete-load', 'load-status']
    },
    {
      id: 'create-dispatch',
      title: 'How to Create a Dispatch',
      category: 'loads',
      icon: MapPin,
      content: 'Create a dispatch by assigning a driver, truck, and trailer to a load.',
      steps: [
        'Navigate to Loads & Dispatching → Loads',
        'Find the load you want to dispatch',
        'Click on the load to open details',
        'Click "Create Dispatch" button',
        'Select a driver from the available list',
        'Assign a truck to the dispatch',
        'Assign a trailer (if needed)',
        'Add dispatch notes or special instructions',
        'Review all dispatch details',
        'Click "Create Dispatch" to confirm'
      ],
      tips: [
        'Check driver ELD hours before creating dispatch',
        'Consider driver location and load pickup time',
        'Verify driver has proper endorsements for the load',
        'Ensure truck and trailer are available and in good condition',
        'Add any special instructions in the dispatch notes'
      ],
      relatedArticles: ['dispatch-board', 'edit-dispatch']
    },
    {
      id: 'dispatch-board',
      title: 'Using the Dispatch Board',
      category: 'loads',
      icon: MapPin,
      content: 'The dispatch board provides a visual overview of all loads and their status.',
      steps: [
        'Navigate to Loads & Dispatching → Dispatch Board',
        'View loads organized by status columns',
        'Drag and drop loads to change status',
        'Click on a load to view details',
        'Use filters to find specific loads',
        'Track load progress in real-time'
      ],
      tips: [
        'Color coding helps identify load priority',
        'Use the map view to see geographic distribution',
        'Filter by driver, customer, or date range',
        'Set up notifications for status changes'
      ],
      relatedArticles: ['create-dispatch', 'dispatches-view', 'excel-dispatch-board']
    },
    {
      id: 'eld-integration',
      title: 'ELD Hours of Service',
      category: 'loads',
      icon: MapPin,
      content: 'Monitor driver hours of service through ELD integration to ensure compliance.',
      steps: [
        'Navigate to ELD from the main menu',
        'View driver hours of service status',
        'Check available driving hours',
        'Monitor duty status changes',
        'Review violations and warnings',
        'Verify compliance before dispatch assignments'
      ],
      tips: [
        'Check ELD hours before assigning loads',
        'Monitor drivers approaching HOS limits',
        'Plan dispatches around driver availability',
        'Address violations promptly',
        'Use ELD data for better route planning'
      ],
      relatedArticles: ['create-dispatch', 'tracking']
    },
    {
      id: 'dispatches-view',
      title: 'Managing Dispatches',
      category: 'loads',
      icon: Truck,
      content: 'View and manage all active dispatches and their status.',
      steps: [
        'Navigate to Loads & Dispatching → Dispatches',
        'View list of all active dispatches',
        'Filter by status (En Route, At Pickup, Loaded, At Delivery, etc.)',
        'Click on a dispatch to view full details',
        'Track driver location in real-time',
        'Monitor pickup and delivery times',
        'View dispatch documents (BOL, POD)',
        'Update dispatch status as needed',
        'Communicate with driver via SMS or phone'
      ],
      tips: [
        'Monitor dispatches approaching pickup/delivery times',
        'Track driver location for accurate ETAs',
        'Update status as load progresses',
        'Communicate proactively with customers',
        'Document any delays or issues',
        'Ensure all documents are uploaded upon completion'
      ],
      relatedArticles: ['create-dispatch', 'tracking', 'dispatch-status']
    },
    {
      id: 'tracking',
      title: 'Real-Time Load Tracking',
      category: 'loads',
      icon: MapPin,
      content: 'Track trucks and loads in real-time using GPS and ELD integration.',
      steps: [
        'Go to Loads & Dispatching → Tracking',
        'View map with all active trucks',
        'Click on a truck to see details',
        'View current location and speed',
        'Check ELD hours of service',
        'See estimated time to destination',
        'View load details and status',
        'Track route and stops',
        'Monitor geofence alerts'
      ],
      tips: [
        'Use tracking to provide accurate ETAs to customers',
        'Monitor driver hours to prevent violations',
        'Set up geofence alerts for pickup/delivery locations',
        'Track multiple loads simultaneously',
        'Share tracking links with customers',
        'Monitor for route deviations or delays'
      ],
      relatedArticles: ['dispatches-view', 'samsara-location', 'eld-integration']
    },
    {
      id: 'delete-load',
      title: 'How to Delete a Load',
      category: 'loads',
      icon: Package,
      content: 'Remove loads that are cancelled or entered in error.',
      steps: [
        'Navigate to Loads & Dispatching → Loads',
        'Find the load you want to delete',
        'Click on the load to open details',
        'Click the "Delete" button',
        'Confirm deletion when prompted',
        'Load is permanently removed'
      ],
      tips: [
        'Cannot delete loads with active dispatches',
        'Consider marking as cancelled instead of deleting',
        'Deletion is permanent and cannot be undone',
        'Document reason for deletion in notes before removing'
      ],
      relatedArticles: ['edit-load', 'load-status', 'cancelled-loads']
    },

    // Safety (Trucks, Drivers, Trailers, Maintenance, Inspections, Accidents, Claims, IFTA)
    {
      id: 'add-truck',
      title: 'How to Add a Truck',
      category: 'safety',
      icon: Truck,
      content: 'Add trucks to your fleet for assignment to loads and drivers.',
      steps: [
        'Go to Safety → Trucks',
        'Click "Add Truck" button',
        'Enter truck details: Unit number, VIN, make, model, year',
        'Add license plate and registration information',
        'Enter insurance details and expiration dates',
        'Upload truck documents (registration, insurance)',
        'Set truck status (Available, In Use, Maintenance, etc.)',
        'Click "Save Truck"'
      ],
      tips: [
        'Use consistent unit numbering for easy identification',
        'Set up expiration reminders for insurance and registration',
        'Upload all required documents for compliance',
        'Keep maintenance records up to date'
      ],
      relatedArticles: ['edit-truck', 'truck-maintenance', 'truck-photos']
    },
    {
      id: 'edit-truck',
      title: 'How to Edit Truck Information',
      category: 'safety',
      icon: Truck,
      content: 'Update truck information as needed.',
      steps: [
        'Navigate to Safety → Trucks',
        'Find the truck using search or list',
        'Click on the truck to open details',
        'Click "Edit" button',
        'Update necessary information',
        'Click "Save Changes"'
      ],
      tips: [
        'Update status when truck goes into maintenance',
        'Keep insurance and registration current',
        'Document any modifications or repairs'
      ],
      relatedArticles: ['add-truck', 'truck-status', 'truck-assignments']
    },
    {
      id: 'truck-maintenance',
      title: 'Managing Truck Maintenance',
      category: 'safety',
      icon: Wrench,
      content: 'Track and schedule maintenance for your trucks.',
      steps: [
        'Go to Safety → Maintenance',
        'Click "Add Maintenance Log"',
        'Select the truck',
        'Choose maintenance type (PM, Repair, Inspection)',
        'Enter service date and details',
        'Add costs and vendor information',
        'Upload receipts and documents',
        'Set next service reminder',
        'Click "Save"'
      ],
      tips: [
        'Schedule preventive maintenance to avoid breakdowns',
        'Track all maintenance costs for budgeting',
        'Set reminders for upcoming service',
        'Keep digital copies of all receipts'
      ],
      relatedArticles: ['maintenance-schedule', 'maintenance-costs', 'service-providers']
    },
    {
      id: 'maintenance-statistics',
      title: 'Maintenance Statistics and Analytics',
      category: 'safety',
      icon: Wrench,
      content: 'View detailed statistics and analytics for fleet maintenance.',
      steps: [
        'Navigate to Maintenance → Statistics',
        'View total maintenance costs by period',
        'Analyze costs by truck or maintenance type',
        'Track maintenance trends over time',
        'Identify high-cost vehicles',
        'Generate maintenance reports',
        'Export data for further analysis'
      ],
      tips: [
        'Review statistics monthly to identify trends',
        'Compare costs across trucks to find outliers',
        'Use data to plan maintenance budgets',
        'Track preventive vs reactive maintenance ratios'
      ],
      relatedArticles: ['truck-maintenance', 'analytics', 'maintenance-costs']
    },
    {
      id: 'maintenance-samsara',
      title: 'Samsara Maintenance Integration',
      category: 'safety',
      icon: Wrench,
      content: 'Access Samsara vehicle diagnostics and maintenance alerts.',
      steps: [
        'Navigate to Maintenance → Samsara',
        'View diagnostic trouble codes (DTCs)',
        'Check engine fault alerts',
        'Monitor vehicle health status',
        'Review maintenance recommendations',
        'Track odometer readings',
        'Schedule maintenance based on alerts'
      ],
      tips: [
        'Address fault codes promptly',
        'Use Samsara data for preventive maintenance',
        'Monitor vehicle health regularly',
        'Keep maintenance records synced with Samsara'
      ],
      relatedArticles: ['truck-maintenance', 'samsara-safety', 'maintenance-statistics']
    },

    // Driver Management
    {
      id: 'add-driver',
      title: 'How to Add a Driver',
      category: 'safety',
      icon: Users,
      content: 'Add drivers to your fleet for load assignments.',
      steps: [
        'Navigate to Safety → Drivers',
        'Click "Add Driver" button',
        'Enter driver personal information',
        'Add CDL number and endorsements',
        'Enter contact information',
        'Upload required documents (CDL, medical card)',
        'Set driver type (Company, Owner Operator)',
        'Configure pay rate and settlement terms',
        'Click "Save Driver"'
      ],
      tips: [
        'Verify CDL and medical card expiration dates',
        'Upload clear copies of all documents',
        'Set up expiration reminders',
        'Document any special qualifications or endorsements'
      ],
      relatedArticles: ['edit-driver', 'driver-documents', 'driver-settlements']
    },
    {
      id: 'edit-driver',
      title: 'How to Edit Driver Information',
      category: 'safety',
      icon: Users,
      content: 'Update driver information and documents.',
      steps: [
        'Go to Safety → Drivers',
        'Find and click on the driver',
        'Click "Edit" button',
        'Update necessary information',
        'Upload new documents if needed',
        'Click "Save Changes"'
      ],
      tips: [
        'Update contact information promptly',
        'Renew documents before expiration',
        'Document any training or certifications',
        'Keep emergency contact information current'
      ],
      relatedArticles: ['create-dispatch', 'dispatch-board', 'driver-availability']
    },
    {
      id: 'driver-settlements',
      title: 'Processing Driver Settlements',
      category: 'accounting',
      icon: DollarSign,
      content: 'Calculate and process driver pay settlements.',
      steps: [
        'Go to Accounting → Driver Payments',
        'Select settlement period',
        'Review completed loads for each driver',
        'Add deductions (fuel, advances, etc.)',
        'Add reimbursements (tolls, repairs, etc.)',
        'Review calculated settlement amount',
        'Generate settlement statement',
        'Mark as paid when processed',
        'Send statement to driver'
      ],
      tips: [
        'Process settlements on a consistent schedule',
        'Document all deductions and reimbursements',
        'Keep receipts for reimbursable expenses',
        'Provide detailed settlement statements'
      ],
      relatedArticles: ['driver-deductions', 'driver-reimbursements', 'settlement-reports']
    },

    // Safety & Compliance
    {
      id: 'inspections',
      title: 'Recording Vehicle Inspections',
      category: 'safety',
      icon: Shield,
      content: 'Document pre-trip and post-trip inspections.',
      steps: [
        'Navigate to Safety → Inspections',
        'Click "Add Inspection"',
        'Select vehicle and driver',
        'Choose inspection type',
        'Complete inspection checklist',
        'Document any defects found',
        'Upload photos if needed',
        'Mark defects as repaired when fixed',
        'Click "Save Inspection"'
      ],
      tips: [
        'Conduct inspections before every trip',
        'Document all defects, even minor ones',
        'Take photos of any damage or issues',
        'Follow up on repairs promptly'
      ],
      relatedArticles: ['accident-reports', 'maintenance-logs', 'compliance']
    },
    {
      id: 'accident-reports',
      title: 'Filing Accident Reports',
      category: 'safety',
      icon: AlertCircle,
      content: 'Document accidents and incidents properly.',
      steps: [
        'Go to Safety → Accident Reports',
        'Click "File Accident Report"',
        'Enter accident date, time, and location',
        'Select involved driver and vehicle',
        'Describe what happened in detail',
        'Add other parties involved',
        'Upload police report and photos',
        'Document injuries and damages',
        'Add insurance claim information',
        'Click "Save Report"'
      ],
      tips: [
        'File reports immediately after incidents',
        'Collect all relevant documentation',
        'Take photos from multiple angles',
        'Get witness statements if possible',
        'Notify insurance promptly'
      ],
      relatedArticles: ['claims', 'insurance', 'safety-compliance']
    },
    {
      id: 'add-trailer',
      title: 'How to Add a Trailer',
      category: 'safety',
      icon: Truck,
      content: 'Add trailers to your fleet for hauling freight.',
      steps: [
        'Navigate to Safety → Trailers',
        'Click "Add Trailer" button',
        'Enter trailer details: Unit number, VIN, trailer type',
        'Add license plate and registration information',
        'Enter insurance details and expiration dates',
        'Upload trailer documents (registration, insurance)',
        'Set trailer status (Available, In Use, Maintenance, etc.)',
        'Click "Save Trailer"'
      ],
      tips: [
        'Use consistent unit numbering system',
        'Track inspection and maintenance schedules',
        'Keep registration and insurance current',
        'Document trailer specifications and capacity'
      ],
      relatedArticles: ['edit-trailer', 'trailer-maintenance', 'trailer-inspections']
    },
    {
      id: 'edit-trailer',
      title: 'How to Edit Trailer Information',
      category: 'safety',
      icon: Truck,
      content: 'Update trailer information and status.',
      steps: [
        'Go to Safety → Trailers',
        'Find the trailer using search or list',
        'Click on the trailer to open details',
        'Click "Edit" button',
        'Update necessary information',
        'Click "Save Changes"'
      ],
      tips: [
        'Update status when trailer goes into maintenance',
        'Keep insurance and registration current',
        'Document any modifications or repairs'
      ],
      relatedArticles: ['add-trailer', 'trailer-status', 'trailer-assignments']
    },
    {
      id: 'claims',
      title: 'Managing Insurance Claims',
      category: 'safety',
      icon: Shield,
      content: 'Track and manage insurance claims for accidents and damages.',
      steps: [
        'Navigate to Safety → Claims',
        'Click "Create Claim"',
        'Link to related accident report',
        'Enter claim number from insurance company',
        'Add claim type (Liability, Physical Damage, Cargo)',
        'Enter claim amount and deductible',
        'Upload claim documents and correspondence',
        'Track claim status (Filed, Under Review, Approved, Denied, Settled)',
        'Add settlement amount when resolved',
        'Click "Save Claim"'
      ],
      tips: [
        'File claims promptly after accidents',
        'Keep all documentation organized',
        'Follow up regularly with insurance adjuster',
        'Document all communications',
        'Track claim resolution timeline'
      ],
      relatedArticles: ['accident-reports', 'insurance', 'safety-compliance']
    },
    {
      id: 'ifta',
      title: 'Managing IFTA Records',
      category: 'safety',
      icon: FileText,
      content: 'Track fuel purchases and mileage for IFTA reporting.',
      steps: [
        'Go to Safety → IFTA',
        'View quarterly IFTA summary',
        'Review fuel purchases by jurisdiction',
        'Check mileage by state/province',
        'Verify fuel tax calculations',
        'Generate IFTA report for filing',
        'Export data for accountant',
        'Submit report to tax authorities'
      ],
      tips: [
        'Keep accurate fuel receipts',
        'Track mileage by jurisdiction',
        'Reconcile fuel purchases regularly',
        'File quarterly reports on time',
        'Maintain records for 4 years',
        'Use GPS tracking for accurate mileage'
      ],
      relatedArticles: ['fuel-cards', 'mileage-tracking', 'tax-compliance']
    },

    // Accounting (Dashboard, Pending Verification, Ready for Billing, Billed, Paid, Driver Payments)
    {
      id: 'accounting-dashboard',
      title: 'Using the Accounting Dashboard',
      category: 'accounting',
      icon: DollarSign,
      content: 'Monitor financial metrics and accounting workflow from the dashboard.',
      steps: [
        'Navigate to Accounting → Dashboard',
        'View total revenue and expenses',
        'Check pending loads awaiting verification',
        'Monitor loads ready for billing',
        'Track billed and paid loads',
        'Review outstanding receivables',
        'Check driver payment status',
        'View financial trends and charts'
      ],
      tips: [
        'Check dashboard daily for workflow status',
        'Monitor aging receivables',
        'Track payment collection rates',
        'Review expense trends regularly'
      ],
      relatedArticles: ['pending-verification', 'ready-billing', 'financial-reports']
    },
    {
      id: 'pending-verification',
      title: 'Verifying Loads for Billing',
      category: 'accounting',
      icon: Clock,
      content: 'Review and verify completed loads before billing customers.',
      steps: [
        'Go to Accounting → Pending Verification',
        'Review list of completed loads',
        'Click on a load to view details',
        'Verify all documents are uploaded (BOL, POD, Rate Confirmation)',
        'Check load details match rate confirmation',
        'Verify any accessorial charges',
        'Review driver settlement if applicable',
        'Click "Approve for Billing"',
        'Load moves to Ready for Billing'
      ],
      tips: [
        'Verify all required documents before approval',
        'Check for detention, layover, or other charges',
        'Ensure POD is signed and legible',
        'Confirm rate matches customer agreement',
        'Add notes for any discrepancies'
      ],
      relatedArticles: ['ready-billing', 'load-documents', 'billing-workflow']
    },
    {
      id: 'ready-billing',
      title: 'Creating Customer Invoices',
      category: 'accounting',
      icon: DollarSign,
      content: 'Generate and send invoices to customers for approved loads.',
      steps: [
        'Navigate to Accounting → Ready for Billing',
        'Select loads to invoice (can select multiple)',
        'Click "Create Invoice"',
        'Review invoice details and line items',
        'Add any additional charges or adjustments',
        'Apply discounts if applicable',
        'Set payment terms and due date',
        'Generate invoice PDF',
        'Send invoice to customer via email',
        'Mark as billed - moves to Billed section'
      ],
      tips: [
        'Batch invoice multiple loads for same customer',
        'Include all supporting documents (BOL, POD)',
        'Set clear payment terms',
        'Use professional invoice template',
        'Send invoices promptly after delivery'
      ],
      relatedArticles: ['billed-loads', 'payment-tracking', 'factoring']
    },
    {
      id: 'billed-loads',
      title: 'Tracking Billed Loads',
      category: 'accounting',
      icon: CheckCircle,
      content: 'Monitor invoiced loads and track payment status.',
      steps: [
        'Go to Accounting → Billed',
        'View all invoiced loads awaiting payment',
        'Check invoice date and due date',
        'Monitor aging (30, 60, 90+ days)',
        'Follow up on overdue invoices',
        'Record partial payments if applicable',
        'Mark as paid when payment received',
        'Load moves to Paid section'
      ],
      tips: [
        'Follow up before due date as reminder',
        'Track aging receivables closely',
        'Send payment reminders for overdue invoices',
        'Document all customer communications',
        'Consider factoring for cash flow'
      ],
      relatedArticles: ['paid-loads', 'payment-collection', 'factoring']
    },
    {
      id: 'paid-loads',
      title: 'Managing Paid Loads',
      category: 'accounting',
      icon: DollarSign,
      content: 'Track and reconcile paid invoices and payments received.',
      steps: [
        'Navigate to Accounting → Paid',
        'View all paid loads',
        'Verify payment amount matches invoice',
        'Record payment date and method',
        'Match payment to bank deposit',
        'Note any short payments or deductions',
        'Export paid loads for accounting software',
        'Archive completed transactions'
      ],
      tips: [
        'Reconcile payments with bank statements',
        'Document any payment discrepancies',
        'Track payment methods (check, ACH, wire)',
        'Export data for tax reporting',
        'Keep records for audit purposes'
      ],
      relatedArticles: ['billed-loads', 'payment-reconciliation', 'financial-reports']
    },
    {
      id: 'owner-operator-pay',
      title: 'Processing Owner Operator Payments',
      category: 'accounting',
      icon: Users,
      content: 'Calculate and process settlements for owner operators.',
      steps: [
        'Go to Accounting → Driver Payments → Owner Operators',
        'Select settlement period (weekly, bi-weekly)',
        'Review completed loads for each owner operator',
        'Calculate gross revenue (rate × percentage)',
        'Subtract fuel advances',
        'Subtract other deductions (insurance, fees)',
        'Add reimbursements (tolls, permits)',
        'Review final settlement amount',
        'Generate settlement statement',
        'Process payment (check, ACH, direct deposit)',
        'Mark as paid and send statement'
      ],
      tips: [
        'Verify all loads are invoiced before settlement',
        'Keep accurate fuel advance records',
        'Document all deductions clearly',
        'Process settlements on consistent schedule',
        'Provide detailed settlement statements'
      ],
      relatedArticles: ['company-driver-pay', 'driver-deductions', 'driver-reimbursements']
    },
    {
      id: 'company-driver-pay',
      title: 'Processing Company Driver Payments',
      category: 'accounting',
      icon: Users,
      content: 'Calculate and process payroll for company drivers.',
      steps: [
        'Navigate to Accounting → Driver Payments → Company Drivers',
        'Select pay period',
        'Review completed loads and miles',
        'Calculate pay (per mile, percentage, or hourly)',
        'Add bonuses or incentives',
        'Subtract deductions (advances, insurance)',
        'Add reimbursements (fuel, tolls)',
        'Calculate taxes and withholdings',
        'Generate pay stub',
        'Process through payroll system',
        'Mark as paid'
      ],
      tips: [
        'Track hours for hourly drivers',
        'Apply correct pay rate per load type',
        'Calculate overtime if applicable',
        'Process payroll on time',
        'Keep detailed pay records'
      ],
      relatedArticles: ['owner-operator-pay', 'driver-deductions', 'payroll-taxes']
    },
    {
      id: 'driver-deductions',
      title: 'Managing Driver Deductions',
      category: 'accounting',
      icon: DollarSign,
      content: 'Track and apply deductions to driver settlements.',
      steps: [
        'Go to Accounting → Driver Payments → Deductions',
        'Click "Add Deduction"',
        'Select driver',
        'Choose deduction type (Fuel Advance, Insurance, Equipment, Damage, Other)',
        'Enter deduction amount',
        'Add description and reference number',
        'Set deduction date',
        'Attach supporting documents',
        'Click "Save Deduction"',
        'Deduction applies to next settlement'
      ],
      tips: [
        'Document all deductions clearly',
        'Get driver acknowledgment for deductions',
        'Keep receipts and supporting documents',
        'Apply deductions to correct settlement period',
        'Review deductions before processing pay'
      ],
      relatedArticles: ['driver-reimbursements', 'owner-operator-pay', 'fuel-advances']
    },
    {
      id: 'driver-reimbursements',
      title: 'Processing Driver Reimbursements',
      category: 'accounting',
      icon: DollarSign,
      content: 'Add reimbursements for driver expenses to settlements.',
      steps: [
        'Navigate to Accounting → Driver Payments → Reimbursements',
        'Click "Add Reimbursement"',
        'Select driver',
        'Choose reimbursement type (Tolls, Scales, Permits, Repairs, Other)',
        'Enter reimbursement amount',
        'Add description and load reference',
        'Upload receipt or proof of expense',
        'Set expense date',
        'Click "Save Reimbursement"',
        'Reimbursement adds to next settlement'
      ],
      tips: [
        'Require receipts for all reimbursements',
        'Verify expenses are legitimate and work-related',
        'Set reimbursement policies clearly',
        'Process reimbursements promptly',
        'Keep digital copies of all receipts'
      ],
      relatedArticles: ['driver-deductions', 'owner-operator-pay', 'expense-policies']
    },

    // Address Book
    {
      id: 'add-customer',
      title: 'Adding a Customer',
      category: 'address-book',
      icon: BookOpen,
      content: 'Add customers to your address book for quick load creation.',
      steps: [
        'Go to Address Book → Customers',
        'Click "Add Customer"',
        'Enter company name and contact information',
        'Add billing address',
        'Set payment terms',
        'Add contact persons',
        'Enter credit limit if applicable',
        'Click "Save Customer"'
      ],
      tips: [
        'Keep contact information up to date',
        'Document payment terms clearly',
        'Add multiple contacts for different departments',
        'Track customer credit limits'
      ],
      relatedArticles: ['edit-customer', 'facility-locations', 'customer-rates']
    },
    {
      id: 'facility-locations',
      title: 'Managing Facility Locations',
      category: 'address-book',
      icon: MapPin,
      content: 'Add pickup and delivery locations for faster load entry.',
      steps: [
        'Navigate to Address Book → Facility Locations',
        'Click "Add Location"',
        'Enter facility name and address',
        'Add operating hours',
        'Enter contact information',
        'Add special instructions (gate codes, dock numbers)',
        'Link to customer if applicable',
        'Click "Save Location"'
      ],
      tips: [
        'Include detailed directions',
        'Note any access restrictions',
        'Add appointment requirements',
        'Document loading/unloading procedures'
      ],
      relatedArticles: ['add-customer', 'load-creation', 'dispatch-notes']
    },
    {
      id: 'factoring-companies',
      title: 'Managing Factoring Companies',
      category: 'address-book',
      icon: DollarSign,
      content: 'Add and manage factoring companies for quick payment processing.',
      steps: [
        'Go to Address Book → Factoring Companies',
        'Click "Add Factoring Company"',
        'Enter company name and contact information',
        'Add factoring agreement details',
        'Enter discount rate and terms',
        'Add contact persons (account manager, funding dept)',
        'Enter payment processing information',
        'Add email for invoice submission',
        'Upload factoring agreement documents',
        'Click "Save Factoring Company"'
      ],
      tips: [
        'Keep factoring agreement terms up to date',
        'Track discount rates for cost analysis',
        'Maintain contact information for quick funding',
        'Document submission requirements',
        'Track funding timelines'
      ],
      relatedArticles: ['invoice-factoring', 'payment-processing', 'cash-flow']
    },
    {
      id: 'service-providers',
      title: 'Managing Service Providers',
      category: 'address-book',
      icon: Wrench,
      content: 'Track maintenance shops, repair facilities, and other service providers.',
      steps: [
        'Navigate to Address Book → Service Providers',
        'Click "Add Service Provider"',
        'Enter provider name and type (Maintenance, Repair, Tire, Towing, etc.)',
        'Add business address and location',
        'Enter contact information',
        'Add operating hours',
        'Enter specialties and services offered',
        'Add pricing information if available',
        'Upload vendor agreements or contracts',
        'Click "Save Service Provider"'
      ],
      tips: [
        'Categorize providers by service type',
        'Keep emergency contact numbers accessible',
        'Track preferred vendors for different services',
        'Document pricing and payment terms',
        'Add notes about service quality and response time',
        'Maintain list of 24/7 emergency providers'
      ],
      relatedArticles: ['maintenance-logs', 'vendor-management', 'emergency-services']
    },

    // Documents
    {
      id: 'upload-documents',
      title: 'Uploading Documents',
      category: 'documents',
      icon: FileText,
      content: 'Upload and manage documents for loads, drivers, and vehicles.',
      steps: [
        'Navigate to Documents',
        'Click "Upload Document"',
        'Select document type (BOL, POD, Invoice, etc.)',
        'Choose file from your computer',
        'Link to load, driver, or vehicle',
        'Add document description',
        'Click "Upload"'
      ],
      tips: [
        'Scan documents in high quality',
        'Use consistent naming conventions',
        'Upload documents promptly',
        'Organize by type and date',
        'Keep backups of important documents'
      ],
      relatedArticles: ['document-types', 'document-search', 'document-retention']
    },

    // Settings
    {
      id: 'user-profile',
      title: 'Managing Your User Profile',
      category: 'settings',
      icon: Users,
      content: 'Update your personal information and preferences.',
      steps: [
        'Click Settings in the navigation',
        'Select "User Profile"',
        'Update your name and contact information',
        'Change password if needed',
        'Set notification preferences',
        'Upload profile photo',
        'Click "Save Changes"'
      ],
      tips: [
        'Use a strong password',
        'Keep contact information current',
        'Enable notifications for important events',
        'Update your profile photo for easy identification'
      ]
    },
    {
      id: 'maintenance-key-hider',
      title: 'Using Key Hider System',
      category: 'safety',
      icon: Wrench,
      content: 'Securely manage and track truck keys for your fleet.',
      steps: [
        'Navigate to Maintenance → Key Hider',
        'View key inventory and locations',
        'Check out keys to drivers',
        'Track key movements and returns',
        'Monitor key availability',
        'Generate key access reports'
      ],
      tips: [
        'Always track who has which keys',
        'Set up key return reminders',
        'Keep secure records of key access',
        'Use key hider for lost key prevention'
      ],
      relatedArticles: ['truck-maintenance', 'add-truck', 'safety']
    },
    {
      id: 'trailer-photos',
      title: 'Managing Trailer Photos',
      category: 'safety',
      icon: Container,
      content: 'Upload and manage photos of trailers for documentation and reference.',
      steps: [
        'Navigate to Maintenance → Trailer Photos',
        'Select a trailer from the list',
        'Upload photos by clicking \"Add Photo\"',
        'Add descriptions or notes to photos',
        'Organize photos by date or category',
        'View photo history for each trailer'
      ],
      tips: [
        'Take photos of trailer condition regularly',
        'Document any damage or issues immediately',
        'Use photos for insurance claims if needed',
        'Keep a visual record of trailer maintenance'
      ],
      relatedArticles: ['add-trailer', 'trailer-maintenance', 'truck-photos']
    },
    {
      id: 'truck-cleaning',
      title: 'Managing Truck Cleaning Records',
      category: 'safety',
      icon: Wrench,
      content: 'Track and schedule truck cleaning for maintenance and presentation.',
      steps: [
        'Navigate to Maintenance → Truck Cleaning',
        'View cleaning history for all trucks',
        'Schedule regular cleaning appointments',
        'Track cleaning costs and vendors',
        'Monitor cleaning frequency',
        'Generate cleaning reports'
      ],
      tips: [
        'Regular cleaning extends truck life',
        'Track cleaning costs for budgeting',
        'Schedule cleaning during downtime',
        'Document cleaning for compliance'
      ],
      relatedArticles: ['truck-maintenance', 'maintenance-statistics', 'fleet']
    },
    {
      id: 'reimbursements-hotel',
      title: 'Managing Hotel Reimbursements',
      category: 'accounting',
      icon: Building2,
      content: 'Process and track hotel expense reimbursements for drivers.',
      steps: [
        'Navigate to Accounting → Expenses → Hotel',
        'Click \"Add Reimbursement\"',
        'Select driver and enter hotel details',
        'Upload hotel receipt',
        'Enter dates and amount',
        'Add notes or trip reference',
        'Click \"Save Reimbursement\"'
      ],
      tips: [
        'Require receipts for all hotel expenses',
        'Verify expenses are legitimate and work-related',
        'Set clear hotel reimbursement policies',
        'Process reimbursements promptly'
      ],
      relatedArticles: ['reimbursements-lyft', 'driver-reimbursements', 'expense-policies']
    },
    {
      id: 'reimbursements-lyft',
      title: 'Managing Lyft Reimbursements',
      category: 'accounting',
      icon: Navigation,
      content: 'Process and track Lyft ride reimbursements for drivers.',
      steps: [
        'Navigate to Accounting → Expenses → Lyft',
        'Click \"Add Reimbursement\"',
        'Select driver and enter ride details',
        'Upload Lyft receipt or screenshot',
        'Enter trip purpose and amount',
        'Add trip reference if applicable',
        'Click \"Save Reimbursement\"'
      ],
      tips: [
        'Require receipts for all Lyft expenses',
        'Verify rides are for legitimate business purposes',
        'Set clear transportation reimbursement policies',
        'Track ride costs for budget analysis'
      ],
      relatedArticles: ['reimbursements-hotel', 'driver-reimbursements', 'expense-policies']
    },
    {
      id: 'reimbursements-airplane',
      title: 'Managing Airplane Ticket Reimbursements',
      category: 'accounting',
      icon: Send,
      content: 'Process and track airplane ticket reimbursements for business travel.',
      steps: [
        'Navigate to Accounting → Expenses → Airplane Tickets',
        'Click \"Add Reimbursement\"',
        'Select driver or employee',
        'Enter flight details and dates',
        'Upload boarding pass and receipt',
        'Enter ticket cost and purpose',
        'Click \"Save Reimbursement\"'
      ],
      tips: [
        'Require boarding passes and receipts',
        'Verify travel is business-related',
        'Book flights in advance when possible',
        'Track travel costs for budget planning'
      ],
      relatedArticles: ['reimbursements-hotel', 'driver-reimbursements', 'expense-policies']
    },
    {
      id: 'deductions-maintenance',
      title: 'Managing Maintenance Deductions',
      category: 'accounting',
      icon: Minus,
      content: 'Track and apply maintenance-related deductions to driver settlements.',
      steps: [
        'Navigate to Accounting → Ded. Maintenance',
        'Click \"Add Deduction\"',
        'Select driver',
        'Enter maintenance deduction amount',
        'Add description and truck reference',
        'Upload maintenance invoice if applicable',
        'Set deduction date',
        'Click \"Save Deduction\"'
      ],
      tips: [
        'Document all maintenance deductions clearly',
        'Get driver acknowledgment for deductions',
        'Keep invoices for all maintenance work',
        'Apply deductions to correct settlement period'
      ],
      relatedArticles: ['deductions', 'truck-maintenance', 'driver-deductions']
    },
    {
      id: 'scheduled-deductions',
      title: 'Managing Scheduled Deductions',
      category: 'accounting',
      icon: Clock,
      content: 'Set up and manage recurring deductions for driver settlements.',
      steps: [
        'Navigate to Accounting → Scheduled Deductions',
        'Click \"Add Scheduled Deduction\"',
        'Select driver',
        'Choose deduction type and frequency',
        'Enter deduction amount',
        'Set start date and end date',
        'Add description and purpose',
        'Click \"Save Scheduled Deduction\"'
      ],
      tips: [
        'Use scheduled deductions for recurring charges',
        'Set clear deduction schedules',
        'Monitor scheduled deduction totals',
        'Review scheduled deductions regularly'
      ],
      relatedArticles: ['deductions', 'driver-deductions', 'settlement-processing']
    },
    {
      id: 'credits',
      title: 'Managing Driver Credits',
      category: 'accounting',
      icon: Plus,
      content: 'Add and manage credits for driver settlements.',
      steps: [
        'Navigate to Accounting → Credits',
        'Click \"Add Credit\"',
        'Select driver',
        'Enter credit amount',
        'Add credit reason and description',
        'Set credit date',
        'Attach supporting documents if needed',
        'Click \"Save Credit\"'
      ],
      tips: [
        'Document all credits clearly',
        'Get driver acknowledgment for credits',
        'Keep records for all credit adjustments',
        'Apply credits to correct settlement period'
      ],
      relatedArticles: ['deductions', 'driver-reimbursements', 'settlement-processing']
    },
    {
      id: 'fuel',
      title: 'Managing Fuel Transactions',
      category: 'accounting',
      icon: Fuel,
      content: 'Track and manage fuel purchases and transactions.',
      steps: [
        'Navigate to Accounting → Fuel',
        'View all fuel transactions',
        'Add new fuel transactions',
        'Enter truck, driver, and fuel details',
        'Upload fuel receipts',
        'Track fuel costs by truck or driver',
        'Generate fuel reports'
      ],
      tips: [
        'Track fuel costs for budget analysis',
        'Monitor fuel efficiency by truck',
        'Keep receipts for all fuel purchases',
        'Use fuel data for route optimization'
      ],
      relatedArticles: ['deductions', 'maintenance-statistics', 'analytics']
    },
    {
      id: 'statements',
      title: 'Managing Driver Statements',
      category: 'accounting',
      icon: FileText,
      content: 'Generate and manage driver settlement statements.',
      steps: [
        'Navigate to Accounting → Statements',
        'Select statement period',
        'Review driver activities and earnings',
        'Add deductions and reimbursements',
        'Generate settlement statement',
        'Review and approve statement',
        'Send statement to driver'
      ],
      tips: [
        'Generate statements on regular schedule',
        'Review all calculations carefully',
        'Provide detailed breakdowns',
        'Keep records of all statements'
      ],
      relatedArticles: ['driver-settlements', 'deductions', 'driver-reimbursements']
    },
    {
      id: 'invoices',
      title: 'Managing Customer Invoices',
      category: 'accounting',
      icon: Receipt,
      content: 'Create and manage customer invoices for completed loads.',
      steps: [
        'Navigate to Accounting → Invoices',
        'Select loads to invoice',
        'Review invoice details and charges',
        'Add accessorial charges if needed',
        'Generate invoice PDF',
        'Send invoice to customer',
        'Track payment status'
      ],
      tips: [
        'Invoice promptly after delivery',
        'Include all supporting documents',
        'Set clear payment terms',
        'Track aging receivables'
      ],
      relatedArticles: ['ready-billing', 'billed-loads', 'payment-tracking']
    },
    {
      id: 'activity-log',
      title: 'Viewing Activity Log',
      category: 'settings',
      icon: Activity,
      content: 'Track and review system activity and changes.',
      steps: [
        'Navigate to Activity Log from main menu',
        'View all system activities',
        'Filter by user, date, or action type',
        'Review specific activity details',
        'Export activity reports if needed',
        'Monitor for unusual activity'
      ],
      tips: [
        'Review activity log regularly',
        'Use filters to find specific events',
        'Export reports for audit purposes',
        'Monitor for security concerns'
      ],
      relatedArticles: ['user-profile', 'users', 'settings']
    }
  ];

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (selectedArticle) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => setSelectedArticle(null)}
          className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
        >
          ← Back to Help Center
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-blue-100 p-3 rounded-lg">
              <selectedArticle.icon className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{selectedArticle.title}</h1>
              <p className="text-slate-600 mt-1">
                {categories.find(c => c.id === selectedArticle.category)?.name}
              </p>
            </div>
          </div>

          <div className="prose max-w-none">
            <p className="text-lg text-slate-700 mb-6">{selectedArticle.content}</p>

            {selectedArticle.steps && selectedArticle.steps.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  Step-by-Step Instructions
                </h2>
                <ol className="space-y-3">
                  {selectedArticle.steps.map((step, index) => (
                    <li key={index} className="flex gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <span className="text-slate-700 pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {selectedArticle.tips && selectedArticle.tips.length > 0 && (
              <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="text-lg font-bold text-yellow-900 mb-3 flex items-center gap-2">
                  💡 Pro Tips
                </h3>
                <ul className="space-y-2">
                  {selectedArticle.tips.map((tip, index) => (
                    <li key={index} className="text-yellow-800 flex gap-2">
                      <span>•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedArticle.relatedArticles && selectedArticle.relatedArticles.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-4">Related Articles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedArticle.relatedArticles.map(relatedId => {
                    const related = articles.find(a => a.id === relatedId);
                    if (!related) return null;
                    return (
                      <button
                        key={relatedId}
                        onClick={() => setSelectedArticle(related)}
                        className="flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                      >
                        <related.icon className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-slate-900">{related.title}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-blue-100 p-3 rounded-lg">
            <HelpCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Help Center</h2>
            <p className="text-slate-600">Find answers and learn how to use Apollo TMS</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search for help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Categories */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Browse by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedCategory === null
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Book className="w-6 h-6 text-slate-600 mb-2" />
              <div className="text-sm font-semibold text-slate-900">All Articles</div>
              <div className="text-xs text-slate-500">{articles.length} articles</div>
            </button>
            {categories.map((category) => {
              const Icon = category.icon;
              const count = articles.filter(a => a.category === category.id).length;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedCategory === category.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center mb-2`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-sm font-semibold text-slate-900">{category.name}</div>
                  <div className="text-xs text-slate-500">{count} articles</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Articles List */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            {selectedCategory 
              ? `${categories.find(c => c.id === selectedCategory)?.name} Articles` 
              : 'All Help Articles'}
          </h3>
          <div className="space-y-2">
            {filteredArticles.map((article) => {
              const Icon = article.icon;
              return (
                <button
                  key={article.id}
                  onClick={() => setSelectedArticle(article)}
                  className="w-full flex items-center gap-4 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                >
                  <div className="bg-white p-2 rounded-lg">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">{article.title}</h4>
                    <p className="text-sm text-slate-600 line-clamp-1">{article.content}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              );
            })}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-12">
              <Book className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No articles found matching your search.</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory(null);
                }}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
